import classNames from "classnames";
import styles from "./css/BigButton.module.css";
import { useCallback, useEffect, useState } from "react";

interface BigButtonProps {
  title: string;
  variant: "normal" | "disable" | "special" | "clear";
  hoverColor?: string;
  buttonNumber?: number;
  handleClick: (e?: React.MouseEvent<HTMLDivElement>) => void;
}

function BigButton(props: BigButtonProps) {
  const [mouseEvent, setMouseEvent] =
    useState<React.MouseEvent<HTMLDivElement>>();

  const handleKeyPress = useCallback(
    (event: KeyboardEvent) => {
      if (
        props.buttonNumber !== undefined &&
        event.key === props.buttonNumber.toString()
      ) {
        props.handleClick(mouseEvent);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.buttonNumber, props.handleClick]
  );

  // 컴포넌트 마운트 시 키 이벤트 리스너 추가, 언마운트 시 제거
  useEffect(() => {
    window.addEventListener("keydown", handleKeyPress);
    return () => {
      window.removeEventListener("keydown", handleKeyPress);
    };
  }, [handleKeyPress]);

  const buttonClass = classNames(styles.bigButton, {
    [styles.styleNormal]: props.variant === "normal",
    [styles.styleDisable]: props.variant === "disable",
    [styles.styleSpecial]: props.variant === "special",
    [styles.styleClear]: props.variant === "clear",
    [styles.withShortcut]: props.buttonNumber !== undefined,
  });

  return (
    <>
      <button
        type="button"
        className={buttonClass}
        // "disable" 은 회색 스타일일 뿐 클릭은 그대로 전달한다
        // (인증 메일 재발송처럼 회색으로 보이지만 누를 수 있는 버튼이 있다)
        aria-keyshortcuts={
          props.buttonNumber !== undefined
            ? props.buttonNumber.toString()
            : undefined
        }
        onClick={(e) => {
          // 마우스로 누른 경우 포커스를 풀어, Enter 단축키와 겹치지 않게 한다
          if (e.detail > 0) e.currentTarget.blur();
          props.handleClick(e as unknown as React.MouseEvent<HTMLDivElement>);
        }}
        onMouseEnter={(e) => {
          setMouseEvent(e as unknown as React.MouseEvent<HTMLDivElement>);
          if (props.hoverColor === undefined) {
            return;
          }
          // 학습 답 버튼: 마우스를 올리면 해당 답의 색으로 채운다
          e.currentTarget.style.borderColor = props.hoverColor;
          e.currentTarget.style.color = "var(--color-on-primary)";
          e.currentTarget.style.backgroundColor = props.hoverColor;
        }}
        onMouseLeave={(e) => {
          if (props.hoverColor === undefined) {
            return;
          }
          e.currentTarget.style.borderColor = "";
          e.currentTarget.style.color = "";
          e.currentTarget.style.backgroundColor = "";
        }}
      >
        {props.buttonNumber !== undefined && (
          <span className={styles.shortcut} aria-hidden>
            {props.buttonNumber}
          </span>
        )}
        <span className={styles.title}>{props.title}</span>
      </button>
    </>
  );
}

export default BigButton;
